import { useEffect, useId, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { btnGhost, btnIcon, inputCls } from '../ui/styles';
import { SettingNumber } from './SettingNumber';
import { useSettings } from './useSettings';
import type { PauseOption } from '../../core/types/site';

/** "4, 6, 8" → [4, 6, 8]; null bij iets ongeldigs (leeg, geen getal, buiten 1–52). */
const parseWeeks = (raw: string): number[] | null => {
  const parts = raw.split(/[,\s]+/).filter(Boolean);
  if (parts.length === 0) return null;
  const weeks = parts.map(Number);
  if (weeks.some(w => !Number.isInteger(w) || w < 1 || w > 52)) return null;
  return Array.from(new Set(weeks)).sort((a, b) => a - b);
};

/** Instellingen › Webshop & verzending › Bewust Abonnement. */
export function SubscriptionSettings() {
  const {
    policy,
    patchPolicy
  } = useSettings();
  const sub = policy.subscription;
  const set = (patch: Partial<typeof sub>) => patchPolicy('subscription', patch);

  // Ritmes als gewone tekst; pas bij verlaten van het veld bewaard, zodat typen niet hapert.
  const intervalsId = useId();
  const [intervalsText, setIntervalsText] = useState(sub.intervals.join(', '));
  const [intervalsError, setIntervalsError] = useState<string | null>(null);
  useEffect(() => setIntervalsText(sub.intervals.join(', ')), [sub.intervals]);
  const commitIntervals = () => {
    const weeks = parseWeeks(intervalsText);
    if (!weeks) {
      setIntervalsError('Vul hele weken in, gescheiden door een komma, bijv. 4, 6, 8.');
      return;
    }
    setIntervalsError(null);
    // Het standaardritme moet een van de keuzes blijven.
    const fallback = weeks.includes(sub.defaultIntervalWeeks) ? sub.defaultIntervalWeeks : weeks[0];
    set({
      intervals: weeks,
      defaultIntervalWeeks: fallback
    });
  };
  const setPause = (i: number, patch: Partial<PauseOption>) => set({
    pauseOptions: sub.pauseOptions.map((p, j) => j === i ? {
      ...p,
      ...patch
    } : p)
  });
  return <Panel title="Bewust Abonnement" description="De regels van het productabonnement. Klanten zien ze in de winkel en in Mijn omgeving." data-id="element-4251" data-mp="2y5ila7wh1l45">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-4252" data-mp="3859dg2xk7o12">
        <SettingNumber label="Minimumbedrag per doos (€)" hint="Slim bijvullen stuurt nooit een kleinere doos. Volgt uit je marge en wat een pakket kost." value={sub.minBoxValue} onChange={v => set({
        minBoxValue: v
      })} min={0} max={500} data-id="element-4253" data-mp="383g7yqyon9un" />
        <SettingNumber label="Hoogstens per product per doos" hint="Meer tegelijk blijft niet vers." value={sub.maxUnitsPerProduct} onChange={v => set({
        maxUnitsPerProduct: v
      })} min={1} max={4} data-id="element-4254" data-mp="3lysdzuiagmqv" />
        <SettingNumber label="Marge vóór het op is (dagen)" hint="Zo lang voordat iets op raakt, ligt het nieuwe er al." value={sub.bufferDays} onChange={v => set({
        bufferDays: v
      })} max={21} data-id="element-4255" data-mp="1wjkjock1sget" />
        <SettingNumber label="Cadeau bij elke … levering" hint="Bijvoorbeeld 3: de derde, zesde, negende levering." value={sub.giftEvery} onChange={v => set({
        giftEvery: v
      })} min={1} max={24} data-id="element-4256" data-mp="1p6bi9lnjcwcs" />
        <Field label="Het cadeau in die doos" hint="Midden in een zin: “er zit … bij”. Bijvuldozen tellen niet mee voor de cadeaustaffel, dus dit mag iets royaler zijn." data-id="element-4257" data-mp="1f6koxnaponea">
          <input value={sub.giftDescription} onChange={e => set({
          giftDescription: e.target.value
        })} onBlur={e => !e.target.value.trim() && set({
          giftDescription: 'een cadeau van Sarah'
        })} placeholder="Bijv. een reisformaat serum" className={inputCls} data-id="element-4258" data-mp="27ynr8r8p2vvw" />
        </Field>
        <SettingNumber label="Huidchecks per jaar (video)" value={sub.videoChecksPerYear} onChange={v => set({
        videoChecksPerYear: v
      })} max={12} data-id="element-4259" data-mp="mjzxyny5edb9" />
        <SettingNumber label="Eerste levering na (dagen)" hint="Na starten of hervatten." value={sub.leadDays} onChange={v => set({
        leadDays: v
      })} max={60} data-id="element-4260" data-mp="2g4xerskuslkl" />
        <SettingNumber label="Aanpassen tot (dagen voor levering)" hint="Daarna ligt de levering vast." value={sub.changeUntilDays} onChange={v => set({
        changeUntilDays: v
      })} max={30} data-id="element-4261" data-mp="26ekwy84fb6i5" />
        <div data-id="element-4262" data-mp="2z307oigiv2no">
          <label htmlFor={intervalsId} className="text-[13px] font-medium text-ink" data-id="element-4263" data-mp="2z1p1yd3inktx">
            Ritmes om uit te kiezen (weken)
          </label>
          <span className="mt-0.5 block text-[12px] leading-snug text-muted" data-id="element-4264" data-mp="2p4st269pgwou">
            Gescheiden door een komma. Het systeem stelt het kortste ritme voor waarbij elke doos het minimum haalt.
          </span>
          <input id={intervalsId} value={intervalsText} onChange={e => setIntervalsText(e.target.value)} onBlur={commitIntervals} inputMode="numeric" aria-invalid={intervalsError ? true : undefined} aria-describedby={intervalsError ? `${intervalsId}-error` : undefined} className={`${inputCls} mt-1.5 sm:max-w-[16rem]`} data-id="element-4265" data-mp="1dri2br2vbfx6" />
          {intervalsError && <span id={`${intervalsId}-error`} role="alert" className="mt-1 block text-[12px] text-clay" data-id="element-4266" data-mp="397gmr9m6gfqf">
              {intervalsError}
            </span>}
        </div>
        <Field label="Standaardritme" hint="Alleen als slim bijvullen niets kan uitrekenen (product zonder “gaat ± … mee”)." data-id="element-4267" data-mp="346skfrsi0cgw">
          <select value={sub.defaultIntervalWeeks} onChange={e => set({
          defaultIntervalWeeks: Number(e.target.value)
        })} className={`${inputCls} sm:max-w-[11rem]`} data-id="element-4268" data-mp="3d8s46ruwdunj">
            {sub.intervals.map(w => <option key={w} value={w} data-id="element-4269" data-mp="2lhvt9wsee02w">
                Elke {w} weken
              </option>)}
          </select>
        </Field>
      </div>

      <fieldset className="mt-6 border-t border-line pt-5" data-id="element-4270" data-mp="tft0avrdrrts">
        <legend className="text-[13px] font-medium text-ink" data-id="element-4271" data-mp="te87pbommiyk">Pauzekeuzes</legend>
        <p className="mt-0.5 text-[12px] text-muted" data-id="element-4272" data-mp="y82wuun03q51">In Mijn omgeving. De middelste wordt voorgesteld bij “ik heb nog genoeg”.</p>
        <ul className="mt-3 space-y-2" data-id="element-4273" data-mp="5infzo8nn317">
          {sub.pauseOptions.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-4274" data-mp="5l04kk8x4bn8" data-mpi={i}>
              <label className="w-24 shrink-0" data-id="element-4275" data-mp="aii6vrsfjsm9">
                <span className="sr-only" data-id="element-4276" data-mp="fgtgq2iq634h">Pauzekeuze {i + 1}: aantal weken</span>
                <NumberInput value={p.weeks} onChange={w => w !== undefined && setPause(i, {
              weeks: w
            })} min={1} max={52} data-id="element-4277" data-mp="fj64iwb5z7pg" />
              </label>
              <label className="min-w-0 flex-1" data-id="element-4278" data-mp="1qux5muagh12o">
                <span className="sr-only" data-id="element-4279" data-mp="1qyb8s4qy1eb6">Pauzekeuze {i + 1}: tekst voor de klant</span>
                <input value={p.label} onChange={e => setPause(i, {
              label: e.target.value
            })} className={inputCls} placeholder="Bijv. 2 weken" data-id="element-4280" data-mp="1vx6240mjel9j" />
              </label>
              <button type="button" onClick={() => set({
            pauseOptions: sub.pauseOptions.filter((_, j) => j !== i)
          })} disabled={sub.pauseOptions.length <= 1} aria-label={`Pauzekeuze ${i + 1} weghalen`} title={sub.pauseOptions.length <= 1 ? 'Er moet minstens één pauzekeuze blijven' : undefined} className={`${btnIcon} mt-0.5`} data-id="element-4281" data-mp="3bf2e0de995e9">
                <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4282" data-mp="2spw0d4sbj3zb" />
              </button>
            </li>)}
        </ul>
        <button type="button" onClick={() => {
        const last = sub.pauseOptions[sub.pauseOptions.length - 1];
        const weeks = (last?.weeks ?? 2) + 2;
        set({
          pauseOptions: [...sub.pauseOptions, {
            weeks,
            label: `${weeks} weken`
          }]
        });
      }} className={`${btnGhost} mt-3`} data-id="element-4283" data-mp="2iqdiquzvruaf">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4284" data-mp="3m03nxjz1k6wr" />
          Pauzekeuze toevoegen
        </button>
      </fieldset>
    </Panel>;
}